import React from 'react';
import { List, Avatar, Empty, Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ActivityRecord } from '@/types/common';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { formatFirestoreTimestamp } from '@/utils/date';
dayjs.extend(relativeTime);

interface ActivityLogListProps {
  activityRecords: ActivityRecord[];
}

const getDisplayDate = (timestamp: any) => {
  const date = formatFirestoreTimestamp(timestamp);
  return date ? dayjs(date).fromNow() : '';
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const ActivityLogList: React.FC<ActivityLogListProps> = ({ activityRecords }) => {
  const { t } = useTranslation(['residents', 'common']);
  if (!activityRecords || activityRecords.length === 0) {
    return <Empty description={t('residents:noActivity') || 'No activity'} />;
  }
  return (
    <List
      itemLayout="horizontal"
      dataSource={activityRecords}
      renderItem={item => (
        <List.Item>
          <List.Item.Meta
            avatar={<Avatar icon={<InfoCircleOutlined />} />}
            title={
              <>
                {`${t(`residents:activityTypes.${item.type}`)} - ${item.performedBy.displayName}`}
                <div style={{ fontSize: 12, color: '#888' }}>
                  {getDisplayDate(item.timestamp)}
                </div>
              </>
            }
            description={
              item.type === 'updated' && item.changedFields ? (
                // New detailed format with changedFields
                <div>
                  {Object.entries(item.changedFields).map(([fieldName, changes]) => (
                    <div key={fieldName} style={{ marginBottom: 8 }}>
                      <Tag color="blue" style={{ marginBottom: 4 }}>
                        {t(`residents:fields.${fieldName}`, fieldName)}
                      </Tag>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        <div>
                          <strong>{t('residents:activityLog.oldValue')}:</strong> {formatValue(changes.oldValue)}
                        </div>
                        <div>
                          <strong>{t('residents:activityLog.newValue')}:</strong> {formatValue(changes.newValue)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : item.type === 'updated' && item.oldValue && item.newValue ? (
                // Legacy format with single field
                <>
                  <div>
                    <strong>{t('residents:activityLog.oldValue')}:</strong> {formatValue(item.oldValue)}
                  </div>
                  <div>
                    <strong>{t('residents:activityLog.newValue')}:</strong> {formatValue(item.newValue)}
                  </div>
                </>
              ) : item.type === 'deleted' && item.oldValue ? (
                // Deleted item
                <div>
                  <strong>{t('residents:activityLog.deletedValue')}:</strong> {formatValue(item.oldValue)}
                </div>
              ) : item.type === 'created' && item.newValue ? (
                // Created item
                <div>
                  <strong>{t('residents:activityLog.createdValue')}:</strong> {formatValue(item.newValue)}
                </div>
              ) : null
            }
          />
        </List.Item>
      )}
    />
  );
};

export default ActivityLogList; 